import { useState } from "react";
import { Link } from "react-router-dom";
import { Icon } from "@/components/ui/Icon";
import { SPACE_META, type TenantKey } from "@/domain/space";
import { eventTimeLabel, type PersonalEvent } from "@/domain/personalEvent";
import { longDateLabel, prodLineText, shortDateLabel, type ProdDay } from "@/domain/mySchedule";
import type { PersonalEventsMode } from "@/hooks/usePersonalEvents";
import type { MockApproval } from "./mock";

/**
 * 내 업무 홈 카드들 (B′ 미리보기 2차, 2026-10-19). 데이터는 index.tsx 가 모아 넘긴다(여기는 표시만).
 *   - TodayEventsCard : 오늘 개인 일정(+ 일정 추가). 생산 현황은 TodayProduction.tsx(대시보드 카드 재사용)
 *   - UpcomingCard    : 다가오는 일정 — +3 / +7 영업일 세그먼트(선택 기억). 생산은 날짜당 한 줄 "현장A 외 N건 · 총 000㎥ (예정)"
 *   - TodoCard        : 할 일(실제, 개인 할 일) + 결재(가상)
 */

export function TenantBadge({ t }: { t: TenantKey }) {
  return <span className={`my-tbadge is-space-${t}`}>{SPACE_META[t].short}</span>;
}

function MineBadge() {
  return <span className="my-tbadge sp-my">내 일정</span>;
}

/** 생산 한 줄의 본문(현장 · 합계) */
function ProdText({ p }: { p: ProdDay }) {
  const t = prodLineText(p);
  return (
    <span className="my-row-title">
      {t.site && <span className="my-prod-site">{t.site}</span>}
      {t.site && <span className="my-prod-dot" aria-hidden="true">·</span>}
      <span className="my-prod-total mono">{t.total}</span>
    </span>
  );
}

function EventRow({ ev, withDate, onOpen }: { ev: PersonalEvent; withDate?: boolean; onOpen: (ev: PersonalEvent) => void }) {
  return (
    <li className="my-row">
      <button type="button" className="my-row-btn" onClick={() => onOpen(ev)} aria-label={`${ev.title} 일정 수정`}>
        {withDate ? <span className="my-row-date mono">{shortDateLabel(ev.date)}</span> : <MineBadge />}
        <span className="my-row-time mono">{eventTimeLabel(ev)}</span>
        <span className="my-row-title">{ev.title}</span>
        <Icon name="pencil" size={14} />
      </button>
    </li>
  );
}

/** 할 일 한 줄 — 체크박스(완료 토글) + 제목(누르면 수정) */
export function TodoRow({
  ev, today, onToggle, onOpen,
}: { ev: PersonalEvent; today: string; onToggle: (ev: PersonalEvent) => void; onOpen: (ev: PersonalEvent) => void }) {
  const overdue = !ev.done && ev.date < today;
  return (
    <li className={`my-row my-todo${ev.done ? " is-done" : ""}`}>
      <input
        type="checkbox"
        className="my-todo-check"
        checked={ev.done}
        onChange={() => onToggle(ev)}
        aria-label={`${ev.title} ${ev.done ? "완료 취소" : "완료"}`}
      />
      <button type="button" className="my-row-btn" onClick={() => onOpen(ev)} aria-label={`${ev.title} 할 일 수정`}>
        <span className={`my-row-date mono${overdue ? " is-overdue" : ""}`}>
          {ev.date === today ? "오늘" : shortDateLabel(ev.date)}
        </span>
        <span className="my-row-title">{ev.title}</span>
        <Icon name="pencil" size={14} />
      </button>
    </li>
  );
}

export function LocalNote({ mode }: { mode: PersonalEventsMode }) {
  if (mode === "local") {
    return (
      <div className="my-local-note">
        <Icon name="info" size={14} />
        미리보기 — 개인 일정·할 일은 이 브라우저에만 저장됩니다(규칙 배포 전)
      </div>
    );
  }
  if (mode === "error") {
    return (
      <div className="my-local-note is-error">
        <Icon name="warning" size={14} />
        개인 일정을 불러오지 못했습니다
      </div>
    );
  }
  return null;
}

// ───────────────────────── ① 오늘 일정 ─────────────────────────

interface TodayEventsProps {
  today: string;
  /** 휴무 사유(영업일이면 null) */
  offReason: string | null;
  /** 오늘 개인 일정(kind event) */
  events: PersonalEvent[];
  eventsMode: PersonalEventsMode;
  onAdd: () => void;
  onOpen: (ev: PersonalEvent) => void;
}

export function TodayEventsCard(p: TodayEventsProps) {
  return (
    <section className="card my-today">
      <div className="card-hd">
        <div>
          <div className="card-title">오늘 일정</div>
          <div className="my-today-date">
            {longDateLabel(p.today)}
            {p.offReason && <span className="badge badge-warn">{p.offReason}</span>}
          </div>
        </div>
        <div className="card-actions">
          <button type="button" className="btn-tiny" onClick={p.onAdd}>
            <Icon name="plus" size={14} />
            일정
          </button>
        </div>
      </div>
      {p.events.length === 0 ? (
        <div className="my-empty">오늘 개인 일정이 없습니다</div>
      ) : (
        <ul className="my-list">
          {p.events.map((ev) => (
            <EventRow key={ev.id} ev={ev} onOpen={p.onOpen} />
          ))}
        </ul>
      )}
      <LocalNote mode={p.eventsMode} />
    </section>
  );
}

// ───────────────────────── ② 다가오는 일정 ─────────────────────────

const UPCOMING_RANGES = [3, 7] as const;
type UpcomingRange = (typeof UPCOMING_RANGES)[number];
const LS_RANGE = "rmc.me.upcomingRange";

function readRange(): UpcomingRange {
  try {
    return localStorage.getItem(LS_RANGE) === "7" ? 7 : 3;
  } catch {
    return 3;
  }
}
function writeRange(r: UpcomingRange) {
  try { localStorage.setItem(LS_RANGE, String(r)); } catch { /* private mode 등 — 이번 창에서만 유지 */ }
}

interface UpcomingProps {
  showJoongang: boolean;
  /** 다음 영업일 7개(최대 범위) — 카드가 선택 범위만큼 자른다 */
  workdays: string[];
  joongangDays: Map<string, ProdDay>;
  joongangLoading: boolean;
  joongangError: string | null;
  showGyeongwon: boolean;
  /** 내일 이후 경원 생산일(OKIS 미러 수주, 날짜순) */
  gyeongwonDays: ProdDay[];
  gyeongwonLoading: boolean;
  gyeongwonError: string | null;
  onAddTodo: () => void;
  /** 내일 이후 개인 일정(kind event, 날짜순) */
  events: PersonalEvent[];
  onOpen: (ev: PersonalEvent) => void;
}

/** 다가오는 개인 일정 최대 표시 */
const MAX_UPCOMING_EVENTS = 5;

export function UpcomingCard(p: UpcomingProps) {
  const [range, setRange] = useState<UpcomingRange>(readRange);
  const pick = (r: UpcomingRange) => { setRange(r); writeRange(r); };

  const dates = p.workdays.slice(0, range);
  const until = dates[dates.length - 1] ?? "";
  const gDays = until ? p.gyeongwonDays.filter((d) => d.date <= until) : [];
  const evs = until ? p.events.filter((e) => e.date <= until) : [];
  const shownEvs = evs.slice(0, MAX_UPCOMING_EVENTS);

  return (
    <section className="card my-upcoming">
      <div className="card-hd">
        <div>
          <div className="card-title">다가오는 일정</div>
          <div className="card-sub">영업일 기준 · 생산은 예정 물량</div>
        </div>
        <div className="card-actions">
          <button type="button" className="btn-tiny" onClick={p.onAddTodo}>
            <Icon name="plus" size={14} />
            할 일
          </button>
          <div className="my-seg" role="group" aria-label="기간">
            {UPCOMING_RANGES.map((r) => (
              <button
                key={r}
                type="button"
                className={`my-seg-btn${range === r ? " is-on" : ""}`}
                aria-pressed={range === r}
                onClick={() => pick(r)}
              >
                +{r}일
              </button>
            ))}
          </div>
        </div>
      </div>

      {p.showJoongang && (
        <div className="my-block">
          <div className="my-block-hd">
            <TenantBadge t="joongang" />
            <span className="my-block-sub">레미콘</span>
            <span className="my-tag">예정</span>
          </div>
          {p.joongangLoading ? (
            <div className="my-block-empty">불러오는 중…</div>
          ) : p.joongangError ? (
            <div className="my-block-empty">{p.joongangError}</div>
          ) : (() => {
            // 예정 없는 날은 줄을 만들지 않는다(사용자 10-20). 전부 없으면 한 줄 안내만.
            const planned = dates.filter((date) => p.joongangDays.has(date));
            return planned.length === 0 ? (
              <div className="my-block-empty">예정된 일정이 없습니다</div>
            ) : (
              <ul className="my-list">
                {planned.map((date) => (
                  <li key={date} className="my-row">
                    <span className="my-row-date mono">{shortDateLabel(date)}</span>
                    <ProdText p={p.joongangDays.get(date)!} />
                  </li>
                ))}
              </ul>
            );
          })()}
        </div>
      )}

      {p.showGyeongwon && (
        <div className="my-block">
          <div className="my-block-hd">
            <TenantBadge t="gyeongwon" />
            <span className="my-block-sub">아스콘</span>
            <span className="my-tag">수주</span>
            {gDays.some((d) => d.mock) && <span className="my-tag is-mock">가상</span>}
          </div>
          {p.gyeongwonLoading ? (
            <div className="my-block-empty">불러오는 중…</div>
          ) : p.gyeongwonError ? (
            <div className="my-block-empty">{p.gyeongwonError}</div>
          ) : gDays.length === 0 ? (
            <div className="my-block-empty">예정된 일정이 없습니다</div>
          ) : (
            <ul className="my-list">
              {gDays.map((d) => (
                <li key={d.date} className="my-row">
                  <span className="my-row-date mono">{shortDateLabel(d.date)}</span>
                  <ProdText p={d} />
                </li>
              ))}
            </ul>
          )}
        </div>
      )}

      <div className="my-block">
        <div className="my-block-hd">
          <MineBadge />
        </div>
        {shownEvs.length === 0 ? (
          <div className="my-block-empty">이 기간 개인 일정 없음</div>
        ) : (
          <ul className="my-list">
            {shownEvs.map((ev) => (
              <EventRow key={ev.id} ev={ev} withDate onOpen={p.onOpen} />
            ))}
          </ul>
        )}
        {evs.length > shownEvs.length && (
          <Link to="/my/tasks" className="my-block-note my-more-link">
            외 {evs.length - shownEvs.length}건 · 일정·할 일에서 보기
          </Link>
        )}
      </div>
    </section>
  );
}

// ───────────────────────── ③ 할 일 · 결재 ─────────────────────────

interface TodoCardProps {
  today: string;
  /** 완료 안 한 할 일(날짜순) — 지난 것·오늘·앞으로 */
  todos: PersonalEvent[];
  approvals: MockApproval[];
  onAddTodo: () => void;
  onToggle: (ev: PersonalEvent) => void;
  onOpen: (ev: PersonalEvent) => void;
}

const MAX_TODOS = 5;

export function TodoCard(p: TodoCardProps) {
  const shown = p.todos.slice(0, MAX_TODOS);
  return (
    <section className="card">
      <div className="card-hd">
        <div>
          <div className="card-title">할 일 · 결재</div>
          <div className="card-sub">할 일은 나만 봅니다 · 결재는 가상 데이터</div>
        </div>
        <div className="card-actions">
          <button type="button" className="btn-tiny" onClick={p.onAddTodo}>
            <Icon name="plus" size={14} />
            할 일
          </button>
        </div>
      </div>
      {shown.length === 0 ? (
        <div className="my-block-empty">남은 할 일이 없습니다</div>
      ) : (
        <ul className="my-list">
          {shown.map((ev) => (
            <TodoRow key={ev.id} ev={ev} today={p.today} onToggle={p.onToggle} onOpen={p.onOpen} />
          ))}
        </ul>
      )}
      {p.todos.length > shown.length && (
        <Link to="/my/tasks" className="my-block-note my-more-link">
          외 {p.todos.length - shown.length}건 · 일정·할 일에서 보기
        </Link>
      )}
      {p.approvals.length > 0 && (
        <div className="my-block">
          <div className="my-block-hd">
            <span className="my-block-sub">결재</span>
            <span className="my-tag is-mock">가상</span>
          </div>
          <ul className="my-list">
            {p.approvals.map((x) => (
              <li key={x.id} className="my-row">
                <TenantBadge t={x.tenant} />
                <span className="my-row-title">{x.title}</span>
              </li>
            ))}
          </ul>
        </div>
      )}
    </section>
  );
}
